import { useState } from 'react';

import { useMutation } from '@tanstack/react-query';

import { requestsClient } from '@/shared/api';
import { initUploadMutation } from '@/shared/api/mutations';

export function useUploadFile() {
  const [isLoading, setIsLoading] = useState(false);

  const upload = async (file: File): Promise<string> => {
    if (!file) return '';
    setIsLoading(true);

    const { url } = await initUploadMutation();
    const formData = new FormData();

    formData.append('file', file);

    const params = {
      method: 'POST',
      body: formData,
    };
    const uploaded = await fetch(url, params);
    const res = await uploaded.json();

    setIsLoading(false);
    return res.id;
  };

  return { upload, isLoading };
}

export const useUploadHook = () => {
  const { upload, isLoading } = useUploadFile();

  const uploadDoc = async (file: File, customFileName: string) => {
    const fileId = await upload(file);

    return { file: { id: fileId }, customFileName };
  };

  return { uploadDoc, isLoading };
};

interface IRespondToRequestMutationParams {
  id: string;
  responseText: string;
  responseAttachments: {
    customFileName: string;
    file: {
      id: string;
    };
  }[];
}

const respondToRequestMutation = async ({
  id,
  responseText,
  responseAttachments,
}: IRespondToRequestMutationParams) => {
  const { vabsRespondPaymentInfoRequest } =
    await requestsClient.respondToRequest({
      input: {
        id,
        responseText,
        responseAttachments,
      },
    });
  return vabsRespondPaymentInfoRequest;
};

export const useSendDocsToReview = () => {
  const { mutateAsync: sendDocs, isPending } = useMutation({
    mutationFn: respondToRequestMutation,
  });

  return { sendDocs, isPending };
};

interface IUpdatePaymentDocsMutationParams {
  id: string;
  note: string;
  attachments: {
    customFileName: string;
    file: {
      id: string;
    };
  }[];
}

const updatePaymentDocsMutation = async ({
  id,
  attachments,
  note,
}: IUpdatePaymentDocsMutationParams) => {
  const { vabsUpdateMyPayment } = await requestsClient.updateMyPayment({
    input: {
      id,
      attachments,
      note,
    },
  });
  return vabsUpdateMyPayment;
};

export const useUpdatePaymentDocs = () => {
  const { mutateAsync: updatePaymentDocs, isPending } = useMutation({
    mutationFn: updatePaymentDocsMutation,
  });

  return { updatePaymentDocs, isPending };
};

export { useComplianceRequestsStore, useComplianceAutoOpenStore } from './requestsList';
